import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { hasPlanAccess, PLAN_FEATURES } from '../lib/pricing';
import UpgradeWall from '../components/UpgradeWall';

interface MonthData {
  month: number;
  accidents: number;
  nearMisses: number;
  daysLost: number;
}

interface ComplianceData {
  totalWorkers: number;
  activeWorkers: number;
  workersWithEpp: number;
  examsOk: number;
  examsWarning: number;
  iessReported: number;
  totalAccidents: number;
}

const MONTH_LABELS = ['E','F','M','A','M','J','J','A','S','O','N','D'];

function fmt2(n: number): string {
  return isNaN(n) || !isFinite(n) ? '—' : n.toFixed(2);
}

function currentMonths() {
  return new Date().getMonth() + 1;
}

// Mismo cálculo que el trigger DB sso_recompute_monthly_stats, para que la
// comparación mes a mes sea consistente con lo que se guarda en sso_monthly_stats.
function computeMonthIndices(accidents: number, daysLost: number, activeWorkers: number, monthNumber: number) {
  const hrs = activeWorkers * 8 * 26 * monthNumber;
  const ifV = hrs > 0 ? (accidents * 200000) / hrs : 0;
  const igV = hrs > 0 ? (daysLost * 200000) / hrs : 0;
  const iaV = (ifV * igV) / 1000;
  return { ifV, igV, iaV };
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function KpiCard({ label, value, color, sub, comparison }: { label: string; value: string | number; color: string; sub?: string; comparison?: React.ReactNode }) {
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <span style={{ fontSize: '1.8rem', fontWeight: 800, color, lineHeight: 1 }}>{value}</span>
      {sub && <span style={{ fontSize: '0.74rem', color: '#64748b' }}>{sub}</span>}
      {comparison}
      <span style={{ fontSize: '0.82rem', color: '#94a3b8', lineHeight: 1.3 }}>{label}</span>
    </div>
  );
}

// Compara el índice del mes actual vs el mes anterior. Para IF/IG/IA, un
// valor más alto es peor (más accidentes/gravedad), así que ↑ = rojo y
// ↓ = verde.
function MonthCompare({ current, previous }: { current: number; previous: number | null }) {
  if (previous === null || isNaN(current) || isNaN(previous)) return null;
  const diff = current - previous;
  const isSame = Math.abs(diff) < 0.005;
  const worsened = diff > 0;
  const arrow = isSame ? '→' : worsened ? '↑' : '↓';
  const color = isSame ? '#64748b' : worsened ? '#ef4444' : '#22c55e';
  return (
    <span style={{ fontSize: '0.74rem', color, fontWeight: 700 }}>
      {arrow} mes anterior: {fmt2(previous)}
    </span>
  );
}

function ComplianceCard({ label, value, total, color, icon }: { label: string; value: number; total: number; color: string; icon: React.ReactNode }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ color, flexShrink: 0 }}>{icon}</div>
        <span style={{ fontSize: '0.85rem', color: '#94a3b8', lineHeight: 1.3 }}>{label}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: '1.6rem', fontWeight: 800, color }}>{value}</span>
        <span style={{ fontSize: '0.9rem', color: '#64748b' }}>/ {total}</span>
        <span style={{ fontSize: '0.8rem', color, fontWeight: 700, marginLeft: 'auto' }}>{pct}%</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: '#0f172a', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.5s' }} />
      </div>
    </div>
  );
}

// ── Inline SVG bar chart ───────────────────────────────────────────────────────

function MonthlyChart({ data }: { data: MonthData[] }) {
  const CHART_H = 160;
  const CHART_W = 780;
  const SLOT_W = CHART_W / 12;
  const BAR_W = 22;
  const BAR_GAP = 4;
  const LEFT_OFF = (SLOT_W - 2 * BAR_W - BAR_GAP) / 2;
  const PAD_TOP = 10;
  const LABEL_Y = PAD_TOP + CHART_H + 22;

  const maxVal = Math.max(1, ...data.map(d => Math.max(d.accidents, d.nearMisses)));

  const barH = (v: number) => (v / maxVal) * CHART_H;
  const barY = (v: number) => PAD_TOP + CHART_H - barH(v);

  const gridLines = [0.25, 0.5, 0.75, 1].map(f => ({
    y: PAD_TOP + CHART_H - f * CHART_H,
    label: Math.round(maxVal * f),
  }));

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${CHART_W} ${LABEL_Y + 10}`} style={{ width: '100%', minWidth: 480, display: 'block' }}>
        {/* Grid lines */}
        {gridLines.map(gl => (
          <g key={gl.y}>
            <line x1={0} y1={gl.y} x2={CHART_W} y2={gl.y} stroke="#334155" strokeWidth={1} strokeDasharray="4 4" />
            <text x={2} y={gl.y - 3} fill="#475569" fontSize={9}>{gl.label}</text>
          </g>
        ))}
        {/* Bars */}
        {data.map((d, i) => {
          const accH = barH(d.accidents);
          const incH = barH(d.nearMisses);
          const accX = i * SLOT_W + LEFT_OFF;
          const incX = accX + BAR_W + BAR_GAP;
          const centerX = i * SLOT_W + SLOT_W / 2;
          return (
            <g key={i}>
              {/* Accidents bar (blue) */}
              {accH > 0 && (
                <rect x={accX} y={barY(d.accidents)} width={BAR_W} height={accH} rx={3} fill="#3b82f6" opacity={0.85} />
              )}
              {accH === 0 && (
                <rect x={accX} y={PAD_TOP + CHART_H - 2} width={BAR_W} height={2} rx={1} fill="#334155" />
              )}
              {/* Incidents bar (orange) */}
              {incH > 0 && (
                <rect x={incX} y={barY(d.nearMisses)} width={BAR_W} height={incH} rx={3} fill="#f59e0b" opacity={0.85} />
              )}
              {incH === 0 && (
                <rect x={incX} y={PAD_TOP + CHART_H - 2} width={BAR_W} height={2} rx={1} fill="#334155" />
              )}
              {/* Value labels */}
              {d.accidents > 0 && (
                <text x={accX + BAR_W / 2} y={barY(d.accidents) - 3} textAnchor="middle" fill="#3b82f6" fontSize={9} fontWeight={700}>{d.accidents}</text>
              )}
              {d.nearMisses > 0 && (
                <text x={incX + BAR_W / 2} y={barY(d.nearMisses) - 3} textAnchor="middle" fill="#f59e0b" fontSize={9} fontWeight={700}>{d.nearMisses}</text>
              )}
              {/* Month label */}
              <text x={centerX} y={LABEL_Y} textAnchor="middle" fill="#64748b" fontSize={11}>{MONTH_LABELS[i]}</text>
            </g>
          );
        })}
        {/* Baseline */}
        <line x1={0} y1={PAD_TOP + CHART_H} x2={CHART_W} y2={PAD_TOP + CHART_H} stroke="#475569" strokeWidth={1} />
      </svg>
      {/* Legend */}
      <div style={{ display: 'flex', gap: 20, justifyContent: 'center', marginTop: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 14, height: 10, borderRadius: 2, background: '#3b82f6' }} />
          <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Accidentes</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 14, height: 10, borderRadius: 2, background: '#f59e0b' }} />
          <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Incidentes</span>
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function SSODashboardPage() {
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const displayCompany = impersonated ?? company;
  const companyId = displayCompany?.id ?? '';

  const thisYear = new Date().getFullYear();
  const yearOptions = [thisYear, thisYear - 1, thisYear - 2];

  const [selectedYear, setSelectedYear] = useState(thisYear);
  const [monthData, setMonthData]       = useState<MonthData[]>([]);
  const [compliance, setCompliance]     = useState<ComplianceData | null>(null);
  const [loading, setLoading]           = useState(true);
  const [prevMonthIndices, setPrevMonthIndices] = useState<{ ifV: number; igV: number; iaV: number } | null>(null);

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);

    const yearStart = `${selectedYear}-01-01`;
    const yearEnd   = `${selectedYear}-12-31`;

    const [
      { data: incidents },
      { data: allWorkers },
      { data: eppAssignments },
    ] = await Promise.all([
      supabase
        .from('incidents')
        .select('incident_type, incident_date, days_lost, iess_reported')
        .eq('company_id', companyId)
        .gte('incident_date', yearStart)
        .lte('incident_date', yearEnd),
      supabase
        .from('workers')
        .select('id, status, last_medical_exam')
        .eq('company_id', companyId),
      supabase
        .from('epp_assignments')
        .select('worker_id, returned, expiry_date')
        .eq('company_id', companyId)
        .eq('returned', false),
    ]);

    const inc = incidents ?? [];
    const workers = allWorkers ?? [];
    const epp = eppAssignments ?? [];

    // Build month data
    const monthMap: MonthData[] = Array.from({ length: 12 }, (_, i) => ({ month: i + 1, accidents: 0, nearMisses: 0, daysLost: 0 }));
    for (const i of inc) {
      const m = new Date(i.incident_date).getMonth(); // 0-indexed
      if (i.incident_type === 'accidente_trabajo') {
        monthMap[m].accidents++;
        monthMap[m].daysLost += i.days_lost ?? 0;
      } else if (i.incident_type === 'incidente') {
        monthMap[m].nearMisses++;
      }
    }
    setMonthData(monthMap);

    // Compliance stats
    const totalWorkers  = workers.length;
    const activeWorkers = workers.filter(w => w.status === 'activo').length;

    const activeWorkerIds = new Set(workers.filter(w => w.status === 'activo').map(w => w.id));
    const today = new Date();
    const in30 = new Date(); in30.setDate(today.getDate() + 30);

    const workersWithEpp = new Set(
      epp
        .filter(a => {
          if (!activeWorkerIds.has(a.worker_id)) return false;
          if (a.expiry_date && new Date(a.expiry_date) < today) return false;
          return true;
        })
        .map(a => a.worker_id)
    ).size;

    // Exams: warning if no exam or exam > 1 year ago
    let examsOk = 0;
    let examsWarning = 0;
    const oneYearAgo = new Date(); oneYearAgo.setFullYear(today.getFullYear() - 1);
    for (const w of workers.filter(wk => wk.status === 'activo')) {
      if (!w.last_medical_exam || new Date(w.last_medical_exam) < oneYearAgo) {
        examsWarning++;
      } else {
        examsOk++;
      }
    }

    const totalAccidents = inc.filter(i => i.incident_type === 'accidente_trabajo').length;
    const iessReported   = inc.filter(i => i.incident_type === 'accidente_trabajo' && i.iess_reported).length;

    setCompliance({ totalWorkers, activeWorkers, workersWithEpp, examsOk, examsWarning, iessReported, totalAccidents });
    setLoading(false);
  }, [companyId, selectedYear]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Previous month comparison (IF/IG/IA) ──────────────────────────────────────

  useEffect(() => {
    if (!companyId || !compliance || selectedYear !== thisYear) {
      setPrevMonthIndices(null);
      return;
    }

    const curMonth  = currentMonths();
    const prevMonth = curMonth === 1 ? 12 : curMonth - 1;
    const prevYear  = curMonth === 1 ? thisYear - 1 : thisYear;

    (async () => {
      const { data: row } = await supabase
        .from('sso_monthly_stats')
        .select('if_index, ig_index, ia_index')
        .eq('company_id', companyId)
        .eq('year', prevYear)
        .eq('month', prevMonth)
        .maybeSingle();

      if (row) {
        setPrevMonthIndices({ ifV: row.if_index ?? 0, igV: row.ig_index ?? 0, iaV: row.ia_index ?? 0 });
        return;
      }

      // Sin estadística guardada para el mes anterior — si está dentro del
      // mismo año seleccionado, lo calculamos igual que el mes actual.
      if (prevYear === selectedYear) {
        const mData = monthData[prevMonth - 1];
        if (mData) {
          setPrevMonthIndices(computeMonthIndices(mData.accidents, mData.daysLost, compliance.activeWorkers, prevMonth));
          return;
        }
      }
      setPrevMonthIndices(null);
    })();
  }, [companyId, selectedYear, monthData, compliance, thisYear]);

  const curMonthIndices = (() => {
    if (selectedYear !== thisYear) return null;
    const curMonth = currentMonths();
    const mData = monthData[curMonth - 1];
    if (!mData || !compliance) return null;
    return computeMonthIndices(mData.accidents, mData.daysLost, compliance.activeWorkers, curMonth);
  })();

  // ── Derived KPIs ──────────────────────────────────────────────────────────────

  const totalAccidents = monthData.reduce((s, d) => s + d.accidents, 0);
  const totalIncidents = monthData.reduce((s, d) => s + d.nearMisses, 0);
  const totalDaysLost  = monthData.reduce((s, d) => s + d.daysLost, 0);

  const meses     = selectedYear === thisYear ? currentMonths() : 12;
  const hours     = (compliance?.activeWorkers ?? 0) * 8 * 26 * meses;
  const IF_val    = hours > 0 ? (totalAccidents * 200000) / hours : NaN;
  const IG_val    = hours > 0 ? (totalDaysLost * 200000) / hours : NaN;
  const IA_val    = (!isNaN(IF_val) && !isNaN(IG_val)) ? (IF_val * IG_val) / 1000 : NaN;

  // ── Render ────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
        <span style={{ color: '#64748b' }}>Cargando dashboard…</span>
      </div>
    );
  }

  const isSuperAdmin = profile?.role === 'superadmin';

  if (!isSuperAdmin && displayCompany && !hasPlanAccess(displayCompany.plan, 'dashboard-sso')) {
    return <UpgradeWall {...PLAN_FEATURES['dashboard-sso']} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#e2e8f0' }}>Dashboard SSO</h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#64748b' }}>Indicadores de Seguridad y Salud Ocupacional — AM 196</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <select
            value={selectedYear}
            onChange={e => setSelectedYear(Number(e.target.value))}
            style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', padding: '8px 12px', fontSize: '0.88rem' }}
          >
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <section>
        <h3 style={{ margin: '0 0 14px', fontSize: '0.88rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Indicadores del año {selectedYear}
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 }}>
          <KpiCard label="Accidentes de trabajo" value={totalAccidents} color="#ef4444" />
          <KpiCard label="Incidentes / casi accidentes" value={totalIncidents} color="#f59e0b" />
          <KpiCard label="Días perdidos" value={totalDaysLost} color="#8b5cf6" />
          <KpiCard
            label="Índice de Frecuencia (IF)" value={fmt2(IF_val)} color="#3b82f6" sub="acc × 200 000 / horas"
            comparison={curMonthIndices && <MonthCompare current={curMonthIndices.ifV} previous={prevMonthIndices?.ifV ?? null} />}
          />
          <KpiCard
            label="Índice de Gravedad (IG)" value={fmt2(IG_val)} color="#06b6d4" sub="días × 200 000 / horas"
            comparison={curMonthIndices && <MonthCompare current={curMonthIndices.igV} previous={prevMonthIndices?.igV ?? null} />}
          />
          <KpiCard
            label="Índice de Accidentabilidad (IA)" value={fmt2(IA_val)} color="#22c55e" sub="IF × IG / 1000"
            comparison={curMonthIndices && <MonthCompare current={curMonthIndices.iaV} previous={prevMonthIndices?.iaV ?? null} />}
          />
        </div>
      </section>

      {/* Monthly chart */}
      <section>
        <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: '20px 24px' }}>
          <h3 style={{ margin: '0 0 18px', fontSize: '0.88rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Evolución mensual {selectedYear}
          </h3>
          <MonthlyChart data={monthData} />
        </div>
      </section>

      {/* Compliance summary */}
      {compliance && (
        <section>
          <h3 style={{ margin: '0 0 14px', fontSize: '0.88rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Resumen de cumplimiento SSO
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
            <ComplianceCard
              label="Trabajadores activos vs registrados"
              value={compliance.activeWorkers}
              total={compliance.totalWorkers}
              color="#22c55e"
              icon={<WorkersIcon />}
            />
            <ComplianceCard
              label="EPP asignado (trabajadores activos)"
              value={compliance.workersWithEpp}
              total={compliance.activeWorkers}
              color="#3b82f6"
              icon={<ShieldIcon />}
            />
            <ComplianceCard
              label="Exámenes médicos al día"
              value={compliance.examsOk}
              total={compliance.activeWorkers}
              color={compliance.examsWarning > 0 ? '#f59e0b' : '#22c55e'}
              icon={<MedIcon />}
            />
            <ComplianceCard
              label="Accidentes reportados al IESS"
              value={compliance.iessReported}
              total={compliance.totalAccidents}
              color={compliance.iessReported < compliance.totalAccidents ? '#ef4444' : '#22c55e'}
              icon={<ReportIcon />}
            />
          </div>
          {compliance.examsWarning > 0 && (
            <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', color: '#f59e0b', fontSize: '0.83rem' }}>
              {compliance.examsWarning} trabajador{compliance.examsWarning !== 1 ? 'es' : ''} con examen médico vencido o sin registro (más de 1 año sin actualizar).
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// ── Icons ──────────────────────────────────────────────────────────────────────

function WorkersIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

function MedIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  );
}

function ReportIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="12" y2="17" />
    </svg>
  );
}
